import VideoService from "../../services/videoService.js";
import { CONNECTION_STATUS } from "../../constants/video";

export const moduleVideo = {
  namespaced: true,

  // ─── State ────────────────────────────────────────────────────────────────
  // Only serializable data lives here.
  // LiveKit Room / MediaStream objects stay as markRaw component-local data.
  state: {
    // Connection lifecycle
    connectionStatus: CONNECTION_STATUS.IDLE, // 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error'
    connectingLabel: "Connecting...",
    error: null,

    // Populated by fetchToken action
    token: null,
    wsUrl: null,
    peerId: null,
    sessionId: null,
    participantId: null,
    isAdmin: false,

    // Local media state (mirrors LiveKit track state)
    localAudioMuted: false,
    localVideoMuted: false,
    mutedByAdmin: false,

    // Recording
    isRecording: false,
    egressId: null,

    // Remote participant metadata keyed by LiveKit identity (peer_id)
    // { [identity]: { name, audioMuted, mutedByAdmin } }
    remoteParticipants: {},

    // Active speakers (identity strings from RoomEvent.ActiveSpeakersChanged)
    activeSpeakers: [],

    // Screen sharing state
    isScreenSharing: false,         // local participant is sharing screen
    screenShareParticipants: [],    // identity[] of everyone currently screen-sharing

    // Meeting duration anchor (Unix ms, set when first connected)
    meetingStartedAt: null,

    // Waiting room — guests pending admission (keyed by LiveKit identity)
    // { [identity]: { identity, name } }
    waitingGuests: {},

    // Whether the local guest is still waiting for admission
    waitingForAdmission: false,

    // Key of the pinned tile: identity for video, 'ss:'+identity for screen share (null = no pin)
    pinnedKey: null,
  },

  // ─── Getters ──────────────────────────────────────────────────────────────
  getters: {
    isConnected: (state) => state.connectionStatus === CONNECTION_STATUS.CONNECTED,
    totalPeers: (state) => 1 + Object.keys(state.remoteParticipants).length,
    sessionId: (state) => state.sessionId,
    participantId: (state) => state.participantId,
    peerId: (state) => state.peerId,
    isAdmin: (state) => state.isAdmin,
    token: (state) => state.token,
    wsUrl: (state) => state.wsUrl,
    isRecording: (state) => state.isRecording,
    egressId: (state) => state.egressId,
    localAudioMuted: (state) => state.localAudioMuted,
    localVideoMuted: (state) => state.localVideoMuted,
    mutedByAdmin: (state) => state.mutedByAdmin,
    connectionStatus: (state) => state.connectionStatus,
    connectingLabel: (state) => state.connectingLabel,
    error: (state) => state.error,
    remoteParticipants: (state) => state.remoteParticipants,
    activeSpeakers: (state) => state.activeSpeakers,
    isScreenSharing: (state) => state.isScreenSharing,
    screenShareParticipants: (state) => state.screenShareParticipants,
    meetingStartedAt: (state) => state.meetingStartedAt,
    waitingGuests: (state) => state.waitingGuests,
    waitingGuestCount: (state) => Object.keys(state.waitingGuests).length,
    waitingForAdmission: (state) => state.waitingForAdmission,
    pinnedKey: (state) => state.pinnedKey,
  },

  // ─── Mutations ────────────────────────────────────────────────────────────
  mutations: {
    SET_TOKEN_DATA(state, payload) {
      state.token = payload.token;
      state.wsUrl = payload.wsUrl;
      state.peerId = payload.peerId;
      state.sessionId = payload.roomId ?? payload.sessionId;
      state.participantId = payload.participantId;
      state.isAdmin = payload.isAdmin;
    },

    SET_CONNECTION_STATUS(state, status) {
      state.connectionStatus = status;
    },

    SET_CONNECTING_LABEL(state, label) {
      state.connectingLabel = label;
    },

    SET_ERROR(state, message) {
      state.error = message;
      state.connectionStatus = CONNECTION_STATUS.ERROR;
    },

    SET_LOCAL_AUDIO_MUTED(state, value) {
      state.localAudioMuted = value;
      if (!value) state.mutedByAdmin = false;
    },

    SET_LOCAL_VIDEO_MUTED(state, value) {
      state.localVideoMuted = value;
    },

    SET_MUTED_BY_ADMIN(state, value) {
      state.mutedByAdmin = value;
      if (value) state.localAudioMuted = true;
    },

    SET_RECORDING(state, { isRecording, egressId }) {
      state.isRecording = isRecording;
      state.egressId = egressId ?? null;
    },

    ADD_REMOTE_PARTICIPANT(state, { identity, name }) {
      state.remoteParticipants = {
        ...state.remoteParticipants,
        [identity]: { name, audioMuted: false, videoMuted: false, mutedByAdmin: false },
      };
    },

    REMOVE_REMOTE_PARTICIPANT(state, identity) {
      const updated = { ...state.remoteParticipants };
      delete updated[identity];
      state.remoteParticipants = updated;
    },

    UPDATE_REMOTE_PARTICIPANT_MUTE(state, { identity, audioMuted, mutedByAdmin }) {
      if (!state.remoteParticipants[identity]) return;
      state.remoteParticipants = {
        ...state.remoteParticipants,
        [identity]: {
          ...state.remoteParticipants[identity],
          audioMuted: audioMuted ?? state.remoteParticipants[identity].audioMuted,
          mutedByAdmin: mutedByAdmin ?? state.remoteParticipants[identity].mutedByAdmin,
        },
      };
    },

    UPDATE_REMOTE_PARTICIPANT_VIDEO(state, { identity, videoMuted }) {
      if (!state.remoteParticipants[identity]) return;
      state.remoteParticipants = {
        ...state.remoteParticipants,
        [identity]: {
          ...state.remoteParticipants[identity],
          videoMuted,
        },
      };
    },

    SET_ACTIVE_SPEAKERS(state, identities) {
      state.activeSpeakers = identities;
    },

    SET_IS_SCREEN_SHARING(state, value) {
      state.isScreenSharing = value;
    },

    SET_SCREEN_SHARE_PARTICIPANTS(state, identities) {
      state.screenShareParticipants = identities;
    },

    SET_MEETING_STARTED_AT(state, timestamp) {
      state.meetingStartedAt = timestamp;
    },

    ADD_WAITING_GUEST(state, { identity, name }) {
      state.waitingGuests = { ...state.waitingGuests, [identity]: { identity, name } };
    },

    REMOVE_WAITING_GUEST(state, identity) {
      const updated = { ...state.waitingGuests };
      delete updated[identity];
      state.waitingGuests = updated;
    },

    SET_WAITING_FOR_ADMISSION(state, value) {
      state.waitingForAdmission = value;
    },

    SET_PINNED_KEY(state, key) {
      // Toggle: pinning the same key again un-pins it
      state.pinnedKey = state.pinnedKey === key ? null : key;
    },

    RESET(state) {
      state.connectionStatus = CONNECTION_STATUS.IDLE;
      state.connectingLabel = "Connecting...";
      state.error = null;
      state.token = null;
      state.wsUrl = null;
      state.peerId = null;
      state.sessionId = null;
      state.participantId = null;
      state.isAdmin = false;
      state.localAudioMuted = false;
      state.localVideoMuted = false;
      state.mutedByAdmin = false;
      state.isRecording = false;
      state.egressId = null;
      state.remoteParticipants = {};
      state.activeSpeakers = [];
      state.isScreenSharing = false;
      state.screenShareParticipants = [];
      state.meetingStartedAt = null;
      state.waitingGuests = {};
      state.waitingForAdmission = false;
      state.pinnedKey = null;
    },
  },

  // ─── Actions ──────────────────────────────────────────────────────────────
  actions: {
    async fetchToken({ commit }, { roomName, participantName, convoId }) {
      commit("SET_CONNECTING_LABEL", "Getting token...");
      commit("SET_CONNECTION_STATUS", CONNECTION_STATUS.CONNECTING);
      try {
        const res = await VideoService.generateToken({ roomName, participantName, convoId });
        commit("SET_TOKEN_DATA", res.data);
        commit("SET_CONNECTING_LABEL", "Connecting to room...");
        return res.data;
      } catch (err) {
        const msg = err?.response?.data?.error || "Failed to get token";
        commit("SET_ERROR", msg);
        throw err;
      }
    },

    async fetchGuestToken({ commit }, { roomName, displayName }) {
      commit("SET_CONNECTING_LABEL", "Getting token...");
      commit("SET_CONNECTION_STATUS", CONNECTION_STATUS.CONNECTING);
      try {
        const res = await VideoService.generateGuestToken({ roomName, displayName });
        commit("SET_TOKEN_DATA", res.data);
        commit("SET_CONNECTING_LABEL", "Connecting to room...");
        return res.data;
      } catch (err) {
        const msg = err?.response?.data?.error || "Failed to get token";
        commit("SET_ERROR", msg);
        throw err;
      }
    },

    async recordLeave({ state }, { isGuest } = {}) {
      const { sessionId, participantId } = state;
      if (!sessionId || !participantId) return;
      try {
        if (isGuest) {
          await VideoService.guestLeaveRoom(sessionId, participantId);
        } else {
          await VideoService.leaveRoom(sessionId, participantId);
        }
      } catch (err) {
        console.error("recordLeave error:", err);
      }
    },

    async startRecording({ commit, state }, roomName) {
      try {
        const res = await VideoService.startRecording(state.sessionId, roomName);
        commit("SET_RECORDING", { isRecording: true, egressId: res.data.egressId });
        return res.data;
      } catch (err) {
        console.error("startRecording error:", err);
        throw err;
      }
    },

    async stopRecording({ commit, state }) {
      try {
        await VideoService.stopRecording(state.sessionId, state.egressId);
        commit("SET_RECORDING", { isRecording: false, egressId: null });
      } catch (err) {
        console.error("stopRecording error:", err);
        throw err;
      }
    },

    async adminMute({ commit }, { sessionId, peerId }) {
      try {
        await VideoService.muteParticipant(sessionId, peerId);
        commit("UPDATE_REMOTE_PARTICIPANT_MUTE", {
          identity: peerId,
          audioMuted: true,
          mutedByAdmin: true,
        });
      } catch (err) {
        console.error("adminMute error:", err);
      }
    },

    async adminKick(_, { sessionId, peerId }) {
      try {
        await VideoService.kickParticipant(sessionId, peerId);
        // Participant removal from state happens via LiveKit ParticipantDisconnected event
      } catch (err) {
        console.error("adminKick error:", err);
      }
    },

    async endMeeting({ state }) {
      try {
        await VideoService.endMeeting(state.sessionId);
        // LiveKit fires DisconnectReason.ROOM_DELETED to all clients
        // cleanup() in VideoRoom.vue handles the rest
      } catch (err) {
        console.error("endMeeting error:", err);
        throw err;
      }
    },

    async admitGuest(_, { roomName, identity }) {
      try {
        await VideoService.admitGuest(roomName, identity);
        // State transition (REMOVE_WAITING_GUEST + ADD_REMOTE_PARTICIPANT + stream creation)
        // is handled by the LiveKit RoomEvent.ParticipantPermissionsChanged handler in VideoRoom.vue
      } catch (err) {
        console.error("admitGuest error:", err);
      }
    },

    async denyGuest({ commit }, { roomName, identity }) {
      try {
        await VideoService.denyGuest(roomName, identity);
        commit("REMOVE_WAITING_GUEST", identity);
      } catch (err) {
        console.error("denyGuest error:", err);
      }
    },

    async fetchVideoCallData(_, message) {
      const text = message?.message || "";
      const match = text.match(/#\/video-call\/([\w-]+)/);
      if (!match) return;

      const roomId = match[1];
      console.log("[video/fetchVideoCallData] Detected video room ID:", roomId);

      try {
        const [statusRes, recordingsRes] = await Promise.all([
          VideoService.getRoomStatus(roomId),
          VideoService.getRecordingsByUuid(roomId),
        ]);
        console.log("[video/fetchVideoCallData] Room status:", statusRes.data);
        console.log("[video/fetchVideoCallData] Room recordings:", recordingsRes.data);
      } catch (err) {
        console.error("[video/fetchVideoCallData] Error for roomId:", roomId, err);
      }
    },
  },
};
